import { useContext, useMemo, useState } from 'react';
import css from './SponsorInfo.module.scss';
import UserContext, { UserProfileContext } from '../../../../common/utils/getContext';
import Skeleton from '../../../../widgets/plug/Skeleton/Skeleton';
import { Img, svgSprite } from '../../../../hybrid/wrappers';
import T from '../../../../common/components/T';
import classNames from 'classnames';
import { skeletonPattern } from './helpers';
import CopyToClipboard from 'react-copy-to-clipboard';

const RenderData = ({ profileData, setAlertData }) => {
    const sponsor = profileData.sponsor;
    const contacts = useMemo(() => Object.entries(sponsor?.contacts || {}).filter((contact) => contact[1]), [sponsor]);
    const firstContacts = contacts?.slice(0, 2);
    const lastContacts = contacts?.slice(2);
    const [showLastContacts, setShowLastContacts] = useState(false);

    const getIcon = (type) => {
        switch (type) {
            case 'phone':
                return 'phone';
            case 'email':
            case 'whatsapp':
            case 'instagram':
            case 'facebook':
            case 'telegram':
            case 'twitter':
            case 'viber':
            case 'tiktok':
                return `${type}-colored`;
            case 'ok':
                return 'odnoklassniki-colored';
            case 'vk':
                return 'vkontakte-colored';
        }
    };

    const contactHander = (type, value) => {
        let href = '';

        switch (type) {
            case 'phone': {
                href = `tel:${value}`;
                break;
            }
            case 'email': {
                href = `mailto:${value}`;
                break;
            }
            case 'whatsapp': {
                href = `https://wa.me/${value}`;
                break;
            }
            case 'instagram': {
                href = `https://instagram.com/${value}`;
                break;
            }
            case 'facebook': {
                href = `https://facebook.com/${value}`;
                break;
            }
            case 'telegram': {
                href = `https://t.me/${value}`;
                break;
            }
            case 'twitter': {
                href = `https://twitter.com/${value}`;
                break;
            }
            case 'viber': {
                href = `viber://pa?chatURI=${value}`;
                break;
            }
            case 'tiktok': {
                href = `www.tiktok.com/${value}`;
                break;
            }
            case 'ok': {
                href = `https://ok.ru/${value}`;
                break;
            }
            case 'vk': {
                href = `https://vk.com/${value}`;
                break;
            }
            default:
                break;
        }

        const link = document.createElement('a');
        link.setAttribute('href', href);
        link.setAttribute('target', '_blank');
        link.click();
        link.remove();
    };

    if (!sponsor) return null;
    return (
        <div className={css.block}>
            <div className={css.blockHead}>
                <div className={css.blockHeadAvatar}>
                    <div className={css.marketingInfo}>
                        <div className={css.packageIcon}>
                            <Img
                                className={classNames(css.package, `rank${sponsor?.marketing_profile?.rank?.id || 0}`)}
                                src={`/src/img/ranks/rank${sponsor?.marketing_profile?.rank?.id || 0}.png`}
                                alt={`rank${sponsor?.marketing_profile?.rank?.id || 0}`}
                            />
                        </div>
                        <div className={css.packageIcon}>
                            <Img
                                className={classNames(css.package, `packageIcon${sponsor?.marketing_profile?.package?.id || 0}`)}
                                src={`/src/img/packages/package${sponsor?.marketing_profile?.package?.id || 0}.png`}
                                alt={`package${sponsor?.marketing_profile?.rank?.id || 0}`}
                            />
                        </div>
                    </div>
                    {sponsor?.image?.links?.preview ? (
                        <Img
                            quality={100}
                            className={css.avatarImage}
                            src={sponsor?.image?.links?.preview}
                            fill
                            alt={'Sponsor avatar'}
                        />
                    ) : (
                        <div className={css.avatarNo}>{svgSprite('plug-user-image', { className: css.avatarNoSvg })}</div>
                    )}
                </div>
                <div className={css.blockHeadInfo}>
                    <p className={css.blockHeadLabel}>
                        <T textName='your_sponsor' defaultStr='Ваш спонсор' page='/office' />
                    </p>
                    <p className={css.blockHeadSponsorName}>{sponsor.full_name}</p>
                </div>
            </div>
            <table className={css.blockBody}>
                <tr>
                    <td>ID:</td>
                    <td>{sponsor.username}</td>
                    <td>
                        <CopyToClipboard
                            onCopy={() =>
                                setAlertData({
                                    type: 'success',
                                    cont: <T textName='successfully_copied' defaultStr='Успешно скопировано' />
                                })
                            }
                            text={sponsor.username}
                        >
                            <div className={css.sponsorCopy}>{svgSprite('file-copy-gt-system')}</div>
                        </CopyToClipboard>
                    </td>
                </tr>
                <tr>
                    <td>
                        <T textName='user_sponsor_package' defaultStr='Пакет' page='/office' />:
                    </td>
                    <td>{sponsor.marketing_profile?.package?.name || '-'}</td>
                    <td></td>
                </tr>
                <tr>
                    <td>
                        <T textName='user_sponsor_qualification' defaultStr='Квалификация' page='/office' />:
                    </td>
                    <td>{sponsor.marketing_profile?.rank?.name || '-'}</td>
                    <td></td>
                </tr>
            </table>
            <div className={classNames(css.blockFooter)}>
                {firstContacts?.map(([type, value]) => (
                    <div key={type} onClick={() => contactHander(type, value)} className={css.sponsorContact}>
                        {svgSprite(getIcon(type), { className: css.sponsorContactSvg })}
                    </div>
                ))}
                {lastContacts?.length ? (
                    <div onClick={() => setShowLastContacts((s) => !s)} className={css.sponsorContact}>
                        {svgSprite('dots-horisontal', { className: css.sponsorContactSvg })}
                        {showLastContacts ? (
                            <div className={css.contactsDropdown}>
                                <div className={css.contactsDropdownContent}>
                                    {lastContacts?.map(([type, value]) => (
                                        <div
                                            key={type}
                                            className={css.contactsDropdownItem}
                                            onClick={() => contactHander(type, value)}
                                        >
                                            <div className={css.contactsDropdownItemIcon}>
                                                {svgSprite(getIcon(type), { className: css.contactsDropdownItemSvg })}
                                            </div>
                                            <p className={css.contactsDropdownItemText}>{value}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default function SponsorInfo() {
    const { setAlertData } = useContext(UserContext);
    const { profileData, loaded } = useContext(UserProfileContext);
    return (
        <Skeleton
            pattern={skeletonPattern}
            wait={!!loaded && !!profileData}
            Component={RenderData}
            componentProps={{ profileData, setAlertData }}
            wrapperStyles={css.skeleton}
        />
    );
}
